// Every query and mutation of a workspace's datasets (where they are). Keys
// are written here only, under ["ws", slug, "datasets", ...]. A dataset itself
// (its rows, tokens) is read and written through ui-core's dataset screen
// (/api/datasets/:id), which keeps its own keys.
//
// Promise:
// - The notices of datasets are Me's (they do not say the workspace): they
//   read every workspace's list again (`invalidateWsDatasets`), only where a
//   list is shown.
// - Making one carries an Idempotency-Key.
import {
  infiniteQueryOptions,
  type QueryClient,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import type {
  DatasetDTO,
  WsDatasetList,
} from "@trameria/cloud-api/workspace-datasets-shared";
import { type Api, idempotencyKeyFor, useApi } from "@trameria/ui-core";

const base = (slug: string) => `/api/w/${encodeURIComponent(slug)}`;

export const wsDatasetsPrefix = (slug: string) =>
  ["ws", slug, "datasets"] as const;

/** Every workspace's datasets are read again (a notice names no workspace). */
export const invalidateWsDatasets = (qc: QueryClient) =>
  qc.invalidateQueries({
    predicate: (q) => q.queryKey[0] === "ws" && q.queryKey[2] === "datasets",
  });

/** The workspace's datasets one sees, the latest made first, a page at a time. */
export const wsDatasetListQuery = (api: Api, slug: string) =>
  infiniteQueryOptions({
    queryKey: [...wsDatasetsPrefix(slug), "list"] as const,
    queryFn: ({ pageParam }) =>
      api<WsDatasetList>(
        `${base(slug)}/datasets${pageParam ? `?before=${encodeURIComponent(pageParam)}` : ""}`,
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });

/** Makes a dataset in a space of the workspace. */
export function useCreateWsDataset(slug: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { spaceId: string; title: string }) =>
      (
        await api<{ dataset: DatasetDTO }>(`${base(slug)}/datasets`, {
          method: "POST",
          body: input,
          idempotencyKey: idempotencyKeyFor(input),
        })
      ).dataset,
    onSettled: () => qc.invalidateQueries({ queryKey: wsDatasetsPrefix(slug) }),
  });
}
